/**
 * LabelConflictsCard
 *
 * QA tool for the currently selected client: finds every Provider (or
 * Organization) that is attached to MORE THAN ONE Label within the SAME Label
 * Type — e.g. a provider sitting in both "North" and "South" of the "Region"
 * Label Type.
 *
 * One card per entity type; `entityType` picks which one this instance runs.
 * All work happens in GET /v2/qa/label-conflicts (admin-only, client-scoped).
 *
 * Nothing runs on its own: like the other QA cards, the scan is explicit —
 * review the card's settings, then press "Run Scan". Switching clients, or
 * changing the Label Type scope, clears the results rather than silently
 * re-scanning.
 *
 * The Label Type dropdown is a SCAN SCOPE, not a post-hoc filter: it is filled
 * on load from GET /v2/qa/label-types (cheap metadata, no scan) so it is usable
 * before the first run, and its value is sent to the scan endpoint.
 *
 * Sorting / paging / filtering are all done client-side here: the endpoint
 * returns the full conflict set for the client in one shot, and DynamicTable
 * gets a pre-sliced page via its { rows, count } data shape.
 */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withStyles } from '@mui/styles';
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Icon,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import axiosInstance from 'utils/axiosUtil';
import { DynamicTable } from 'components';

const styles = (theme) => ({
  card: {
    marginBottom: theme.spacing(3),
  },
  cardInnerHeader: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  cardTitle: {
    flex: 1,
  },
  description: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  controls: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: 4,
  },
  typeSelect: {
    minWidth: 240,
  },
  // "All Label Types" sits at the root; the individual types are indented under
  // it so the dropdown reads as a hierarchy — picking the root means all of them.
  labelTypeAll: {
    fontWeight: 600,
  },
  labelTypeOption: {
    paddingLeft: theme.spacing(3),
  },
  summary: {
    marginLeft: 'auto',
    fontSize: 13,
    color: theme.palette.text.secondary,
    textAlign: 'right',
  },
  summaryCount: {
    fontSize: 20,
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
  },
  summaryClean: {
    color: '#2e7d32',
  },
  summaryDirty: {
    color: '#c62828',
  },
  multiChip: {
    marginLeft: 6,
    height: 18,
    fontSize: 10,
    fontWeight: 600,
    backgroundColor: '#fff8e1',
    color: '#8d6e00',
  },
  labelLink: {
    color: theme.palette.primary.main,
  },
  labelSep: {
    color: '#999999',
  },
  errorMsg: {
    fontSize: 13,
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
  },
  truncatedMsg: {
    fontSize: 12,
    color: '#8d6e00',
    backgroundColor: '#fff8e1',
    padding: theme.spacing(1),
    borderRadius: 4,
    marginBottom: theme.spacing(1),
  },
  noData: {
    padding: theme.spacing(3),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    fontSize: 13,
  },
});

// Returns a FRESH object every call. DynamicTable's onOrderChange mutates
// query.order (and aliases it into query.baseOrder) in place, so a shared
// module-level literal would leak one card's sort into the other card and
// permanently redefine the "default" order for the session.
// Sentinel for the "no scope, scan everything" dropdown option.
const ALL_LABEL_TYPES = 'all';

function defaultQuery() {
  return {
    limit: 25,
    offset: 0,
    order: [['namespace_name', 'ASC']],
  };
}

// query.order entries are ['col', 'ASC'] for flat keys and
// ['a', 'b', 'ASC'] for dotted paths — mirror DynamicTable's own decoding.
function decodeOrder(order) {
  if (!Array.isArray(order)) return [];
  return order
    .filter((o) => Array.isArray(o) && o.length >= 2)
    .map((o) => ({
      key: o.slice(0, o.length - 1).join('.'),
      dir: String(o[o.length - 1]).toUpperCase() === 'DESC' ? -1 : 1,
    }));
}

function compareValues(a, b) {
  const aNull = a === null || a === undefined || a === '';
  const bNull = b === null || b === undefined || b === '';
  if (aNull && bNull) return 0;
  if (aNull) return 1; // empties always sink
  if (bNull) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true });
}

class LabelConflictsCard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasRun: false,
      loading: false,
      error: null,
      rows: [],
      scanned: null,
      // Scan-scope dropdown, loaded independently of any scan.
      labelTypes: [],
      labelTypesLoading: false,
      // ALL_LABEL_TYPES rather than '' so the dropdown actually displays the
      // selection — MUI renders an empty-string value as "nothing selected".
      namespaceId: ALL_LABEL_TYPES,
      // A card setting, not a result — deliberately preserved across scans.
      hideMultiAllowed: false,
      truncated: false,
      total: 0,
      limit: 0,
      query: defaultQuery(),
    };
    // Monotonic request id. Switching clients (or double-clicking Rescan)
    // starts a second scan while the first is in flight; without this, a slow
    // response for the OLD client can land last and render another client's
    // providers under the new client's header.
    this.requestSeq = 0;
    this.mounted = false;
  }

  componentDidMount() {
    this.mounted = true;
    this.loadLabelTypes();
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  componentDidUpdate(prevProps) {
    const { app } = this.props;
    if (app.selectedClient !== prevProps.app.selectedClient) {
      // Results belong to the client they were scanned for — drop them and
      // wait for the user to press Run Scan again. Bumping requestSeq also
      // orphans any scan still in flight for the previous client.
      this.requestSeq += 1;
      this.setState({
        ...this.clearedResults(),
        hasRun: false,
        loading: false,
        labelTypes: [],
        namespaceId: ALL_LABEL_TYPES,
      });
      this.loadLabelTypes();
    }
  }

  onQueryChange = (query) => {
    this.setState({ query });
  };

  // The dropdown scopes the SCAN, so a change invalidates whatever is on
  // screen — clear back to the un-run state instead of showing results that
  // no longer match the selected scope.
  onLabelTypeChange = (event) => {
    this.requestSeq += 1;
    this.setState({
      ...this.clearedResults(),
      namespaceId: event.target.value,
      hasRun: false,
      loading: false,
    });
  };

  onHideMultipleChange = (event) => {
    const { checked } = event.target;
    this.setState((prev) => ({
      hideMultiAllowed: checked,
      query: { ...prev.query, offset: 0 },
    }));
  };

  isProvider() {
    return this.props.entityType !== 'organization';
  }

  // Everything derived from a scan. `hideMultiAllowed`, `labelTypes` and
  // `namespaceId` are intentionally absent: those are settings the user picks
  // BEFORE running, so they survive a reset.
  clearedResults() {
    return {
      error: null,
      rows: [],
      scanned: null,
      truncated: false,
      total: 0,
      limit: 0,
      query: defaultQuery(),
    };
  }

  // Cheap metadata read that fills the scan-scope dropdown. Deliberately NOT
  // gated behind Run Scan — without it the dropdown would be empty and
  // unusable on arrival, which is the whole point of picking a scope first.
  loadLabelTypes = async () => {
    const { app, user } = this.props;
    const clientId = app.selectedClient;
    if (!clientId || !user?.is_admin) return;

    this.setState({ labelTypesLoading: true });
    try {
      const res = await axiosInstance.get('/v2/qa/label-types', {
        params: {
          clientId,
          entityType: this.isProvider() ? 'provider' : 'organization',
        },
      });
      if (!this.mounted || this.props.app.selectedClient !== clientId) return;
      this.setState({
        labelTypesLoading: false,
        labelTypes: res.data?.labelTypes || [],
      });
    } catch (err) {
      if (!this.mounted || this.props.app.selectedClient !== clientId) return;
      this.setState({
        labelTypesLoading: false,
        error: err.response?.data?.error || err.message || 'Could not load label types',
      });
    }
  };

  fetchData = async () => {
    const { app, user } = this.props;
    const clientId = app.selectedClient;
    if (!clientId || !user?.is_admin) return;

    // Capture the scope up front so the request can never disagree with the
    // dropdown value the user actually pressed Run Scan on.
    const { namespaceId } = this.state;

    this.requestSeq += 1;
    const seq = this.requestSeq;

    this.setState({ ...this.clearedResults(), hasRun: true, loading: true });

    // Drop anything that is no longer the newest in-flight scan, or that came
    // back after the client changed underneath us.
    const isCurrent = () =>
      this.mounted &&
      seq === this.requestSeq &&
      this.props.app.selectedClient === clientId;

    try {
      const res = await axiosInstance.get('/v2/qa/label-conflicts', {
        params: {
          clientId,
          entityType: this.isProvider() ? 'provider' : 'organization',
          ...(namespaceId === ALL_LABEL_TYPES ? {} : { namespaceId }),
        },
      });
      if (!isCurrent()) return;
      this.setState({
        loading: false,
        rows: res.data?.rows || [],
        scanned: res.data?.scanned || null,
        truncated: !!res.data?.truncated,
        total: res.data?.total || 0,
        limit: res.data?.limit || 0,
      });
    } catch (err) {
      if (!isCurrent()) return;
      this.setState({
        loading: false,
        error: err.response?.data?.error || err.message || 'Scan failed',
      });
    }
  };

  // Filter -> sort -> slice. DynamicTable renders exactly what it is handed,
  // so the page slice has to happen here.
  visibleRows() {
    const { rows, hideMultiAllowed, query } = this.state;

    // The Label Type scope is applied server-side, so only the view filter
    // runs here.
    let filtered = rows;
    if (hideMultiAllowed) {
      filtered = filtered.filter((r) => !r.allows_multiple);
    }

    const sorts = decodeOrder(query.order);
    if (sorts.length > 0) {
      filtered = filtered.slice().sort((a, b) => {
        for (let i = 0; i < sorts.length; i += 1) {
          const { key, dir } = sorts[i];
          const cmp = compareValues(a[key], b[key]);
          if (cmp !== 0) return cmp * dir;
        }
        return 0;
      });
    }

    const offset = query.offset || 0;
    return {
      count: filtered.length,
      rows: filtered.slice(offset, offset + query.limit),
    };
  }

  columns() {
    const { classes } = this.props;
    const isProvider = this.isProvider();

    return [
      {
        label: isProvider ? 'Provider' : 'Organization',
        key: 'entity_name',
        format: (value, row) => (
          <Link
            to={
              isProvider
                ? `/providers/${row.entity_id}`
                : `/organizations/${row.entity_id}`
            }
            target='_blank'
            className={classes.labelLink}
          >
            {value}
          </Link>
        ),
      },
      {
        label: 'NPI',
        key: 'npi',
        style: { width: 110 },
        format: (value) => value || '—',
      },
      {
        label: 'Label Type',
        key: 'namespace_name',
        format: (value, row) => (
          <>
            {value}
            {row.allows_multiple && (
              <Chip
                size='small'
                label='multiple allowed'
                className={classes.multiChip}
              />
            )}
          </>
        ),
      },
      {
        label: 'Labels',
        key: 'label_count',
        style: { width: 70 },
      },
      {
        label: 'Labels In This Type',
        key: 'labels',
        sortable: false,
        format: (value) => (
          <>
            {(value || []).map((label, i) => (
              <React.Fragment key={label.id}>
                {i > 0 && <span className={classes.labelSep}>, </span>}
                <Link
                  to={`/labels/${label.id}`}
                  target='_blank'
                  className={classes.labelLink}
                >
                  {label.name}
                </Link>
              </React.Fragment>
            ))}
          </>
        ),
      },
    ];
  }

  renderSummary(count) {
    const { classes } = this.props;
    const { scanned, loading, hasRun } = this.state;
    if (loading || !hasRun) return null;

    return (
      <div className={classes.summary}>
        <span
          className={`${classes.summaryCount} ${
            count > 0 ? classes.summaryDirty : classes.summaryClean
          }`}
        >
          {count.toLocaleString()}
        </span>
        {` conflict${count === 1 ? '' : 's'}`}
        {scanned && (
          <div style={{ fontSize: 11 }}>
            {`scanned ${scanned.labels.toLocaleString()} labels in ${scanned.labelTypes.toLocaleString()} label types`}
          </div>
        )}
      </div>
    );
  }

  render() {
    const { classes, app, user } = this.props;
    const {
      hasRun,
      loading,
      error,
      labelTypes,
      labelTypesLoading,
      namespaceId,
      hideMultiAllowed,
      truncated,
      total,
      limit,
      query,
    } = this.state;

    if (!user?.is_admin) return null;

    const isProvider = this.isProvider();
    const entityWord = isProvider ? 'Providers' : 'Organizations';
    const data = this.visibleRows();

    return (
      <Card className={classes.card} elevation={2}>
        <CardContent>
          <div className={classes.cardInnerHeader}>
            <Typography
              component='h2'
              variant='h6'
              color='primary'
              gutterBottom
              className={classes.cardTitle}
            >
              {`${entityWord} in Multiple Labels of One Label Type`}
            </Typography>
            <Button
              color='primary'
              size='small'
              onClick={this.fetchData}
              disabled={loading || !app.selectedClient}
              startIcon={
                loading ? (
                  <CircularProgress size={16} color='inherit' />
                ) : (
                  <Icon fontSize='small'>check_circle</Icon>
                )
              }
            >
              {loading ? 'Scanning…' : 'Run Scan'}
            </Button>
          </div>

          <Typography className={classes.description}>
            {`Every ${
              isProvider ? 'provider' : 'organization'
            } in the selected client assigned to two or more Labels that share a Label Type. Label Types configured to allow multiple selections are flagged rather than hidden — use the checkbox to drop them.`}
          </Typography>

          <div className={classes.controls}>
            <FormControl size='small' className={classes.typeSelect}>
              <InputLabel>Label Type to scan</InputLabel>
              <Select
                value={namespaceId}
                label='Label Type to scan'
                onChange={this.onLabelTypeChange}
                disabled={loading || labelTypesLoading}
              >
                <MenuItem value={ALL_LABEL_TYPES} className={classes.labelTypeAll}>
                  {labelTypes.length > 0
                    ? `All Label Types (${labelTypes.length})`
                    : 'All Label Types'}
                </MenuItem>
                {labelTypesLoading && (
                  <MenuItem disabled value='loading' className={classes.labelTypeOption}>
                    Loading…
                  </MenuItem>
                )}
                {!labelTypesLoading && labelTypes.length === 0 && (
                  <MenuItem disabled value='none' className={classes.labelTypeOption}>
                    No Label Type has more than one label
                  </MenuItem>
                )}
                {labelTypes.map((t) => (
                  <MenuItem key={t.id} value={t.id} className={classes.labelTypeOption}>
                    {`${t.name} (${t.label_count} labels`}
                    {t.allows_multiple ? ', multiple allowed)' : ')'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {labelTypesLoading && <CircularProgress size={18} />}

            <FormControlLabel
              control={
                <Checkbox
                  size='small'
                  checked={hideMultiAllowed}
                  onChange={this.onHideMultipleChange}
                  disabled={loading}
                />
              }
              label={
                <Typography style={{ fontSize: 13 }}>
                  Hide Label Types that allow multiple
                </Typography>
              }
            />

            {this.renderSummary(data.count)}
          </div>

          {error && <Typography className={classes.errorMsg}>{error}</Typography>}

          {truncated && (
            <Typography className={classes.truncatedMsg}>
              {`Showing the first ${limit.toLocaleString()} of ${total.toLocaleString()} conflicts. Scan a single Label Type to see the rest.`}
            </Typography>
          )}

          {!app.selectedClient ? (
            <Typography className={classes.noData}>
              Select a client to run this scan.
            </Typography>
          ) : !hasRun ? (
            <Typography className={classes.noData}>
              Review the settings above, then press Run Scan.
            </Typography>
          ) : (
            <DynamicTable
              columns={this.columns()}
              data={loading ? { placeholder: true, showLoading: true } : data}
              idKey='id'
              query={query}
              onQueryChange={this.onQueryChange}
              showFilters={false}
              noDataMessage={`No ${
                isProvider ? 'providers' : 'organizations'
              } are in multiple labels of the same label type.`}
            />
          )}
        </CardContent>
      </Card>
    );
  }
}

LabelConflictsCard.propTypes = {
  classes: PropTypes.object.isRequired,
  app: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  entityType: PropTypes.oneOf(['provider', 'organization']).isRequired,
};

function mapStateToProps(state) {
  const { app, user } = state;
  return { app, user: user || {} };
}

const styled = withStyles(styles)(LabelConflictsCard);
const connected = connect(mapStateToProps)(styled);
export { connected as LabelConflictsCard };
